import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { 
  Phone, Mail, MessageCircle, Home, Calendar, Square, 
  Thermometer, Car, Sun, Moon,
  Train, Download, Bed, Bath, Zap, Wifi, Shield, 
  Key, Eye, Users, Coffee, Menu, ArrowUp,
  FileText, Navigation, CheckCircle, Building, Trees, Heart
} from 'lucide-react';

// Import data and translations
import listingData from './content/listing.json';
import huTranslations from './content/i18n/hu.json';

// Fix Leaflet icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [showGallery, setShowGallery] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const t = huTranslations;

  // Dark mode effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  // Prevent body scroll when gallery is open - better approach
  useEffect(() => {
    if (showGallery) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }, [showGallery]);

  // Scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sticky mobile CTA bar
  const MobileCTA = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-2 z-40 md:hidden">
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => window.location.href = `tel:${listingData.contact.phone}`}
          className="bg-blue-600 text-white py-3 rounded-lg flex items-center justify-center gap-2"
        >
          <Phone className="w-4 h-4" />
          {t.cta.call}
        </button>
        <button
          onClick={() => window.open(`https://wa.me/${listingData.contact.whatsapp}`, '_blank')}
          className="bg-green-600 text-white py-3 rounded-lg flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </button>
        <button
          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          className="bg-gray-600 text-white py-3 rounded-lg flex items-center justify-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          {t.cta.bookViewing}
        </button>
      </div>
    </div>
  );

  // Header navigation
  const Header = () => (
    <header className="fixed top-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm z-50 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-bold">{listingData.title}</h1>
          <div className="hidden md:flex items-center gap-6">
            {['overview', 'features', 'gallery', 'location', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => document.getElementById(item)?.scrollIntoView({ behavior: 'smooth' })}
                className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {t.nav[item as keyof typeof t.nav]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${listingData.contact.phone}`}
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium"
            >
              <Phone className="w-5 h-5" />
              {listingData.contact.phone}
            </a>
          </div>

          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-3">
              {['overview', 'features', 'gallery', 'location', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    document.getElementById(item)?.scrollIntoView({ behavior: 'smooth' });
                    setShowMobileMenu(false);
                  }}
                  className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  {t.nav[item as keyof typeof t.nav]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );

  // Hero Section
  const Hero = () => (
    <section id="overview" className="relative h-screen min-h-[700px] flex items-center justify-center">
      <div className="absolute inset-0">
        <img
          src={listingData.photos[0]}
          alt={listingData.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"></div>
      </div>
      
      <div className="relative z-10 text-white text-center px-4 max-w-5xl mx-auto">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl md:text-6xl font-bold mb-4 text-shadow-lg"
        >
          {listingData.title}
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-xl md:text-2xl mb-8 text-shadow-lg"
        >
          {listingData.tagline}
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl p-6 md:p-8 text-gray-900 dark:text-white"
        >
          <div className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-6">
            {listingData.priceFormatted}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {listingData.descriptionShort.map((item, idx) => (
              <div key={idx} className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 text-sm">
                {item}
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button 
              onClick={() => window.location.href = `tel:${listingData.contact.phone}`}
              className="btn-primary flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              {t.cta.call}
            </button>
            <button 
              onClick={() => window.open(`https://wa.me/${listingData.contact.whatsapp}`, '_blank')}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              {t.cta.whatsapp}
            </button>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              {t.cta.bookViewing}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );

  // Facts Grid
  const FactsGrid = () => (
    <section className="section-padding container-custom">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Square className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-2xl font-bold">{listingData.sizeSqm} {t.labels.sqm}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.size}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Bed className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-2xl font-bold">{listingData.rooms}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.rooms}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Bath className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-2xl font-bold">{listingData.bathrooms}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.bathrooms}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Home className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-2xl font-bold">{listingData.balconies}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.balconies}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Calendar className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-xl font-bold">{listingData.handover}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.handover}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Thermometer className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-sm font-bold">{listingData.heatingCooling}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.heating}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Zap className="w-10 h-10 text-green-600 dark:text-green-400 mx-auto mb-3" />
          <div className="text-2xl font-bold">{listingData.energyRating}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.energy}</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.05 }}
          className="card text-center"
        >
          <Car className="w-10 h-10 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
          <div className="text-sm font-bold">{listingData.condoInfo.parking}</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm">{t.labels.parking}</div>
        </motion.div>
      </div>
    </section>
  );

  // Gallery Component
  const Gallery = () => (
    <section id="gallery" className="section-padding container-custom">
      <h2 className="text-3xl font-bold mb-8">{t.nav.gallery}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {listingData.photos.slice(0, 8).map((photo, idx) => (
          <motion.div
            key={idx}
            whileHover={{ scale: 1.05 }}
            onClick={() => {
              setCurrentPhotoIndex(idx);
              setShowGallery(true);
            }}
            className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
          >
            <img
              src={photo}
              alt={`Property photo ${idx + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <Eye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-8 h-8" />
            </div>
            {idx === 7 && listingData.photos.length > 8 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  +{listingData.photos.length - 8}
                </span>
              </div>
            )}
          </motion.div>
        ))}
      </div>
      
      <Lightbox
        open={showGallery}
        close={() => setShowGallery(false)}
        index={currentPhotoIndex}
        slides={listingData.photos.map(photo => ({ src: photo }))}
        on={{
          view: ({ index }) => setCurrentPhotoIndex(index)
        }}
      />
    </section>
  );

  // Video Section
  const VideoSection = () => {
    const [videoErrors, setVideoErrors] = useState<{[key: number]: string}>({});

    return (
      <section className="section-padding container-custom">
        <h2 className="text-3xl font-bold mb-8">Videók</h2>
        <div className="space-y-8">
          {listingData.video.map((videoData, index) => (
            <div key={index} className="space-y-3">
              <h3 className="text-xl font-semibold">{videoData.title}</h3>
              {videoData.description && (
                <p className="text-gray-600 dark:text-gray-400">{videoData.description}</p>
              )}
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                <video
                  className="w-full h-full object-cover"
                  controls
                  loop
                  playsInline
                  preload="auto"
                  onError={(e) => {
                    console.error(`Video ${index} error:`, e);
                    setVideoErrors({...videoErrors, [index]: 'Hiba a videó betöltése során'});
                  }}
                  onLoadedData={() => {
                    console.log(`Video ${index} loaded successfully`);
                  }}
                  onCanPlay={() => {
                    console.log(`Video ${index} can play`);
                  }}
                >
                  <source src={videoData.src} type="video/mp4" />
                  A böngésző nem támogatja a videó lejátszást.
                </video>
                {videoErrors[index] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white">
                    {videoErrors[index]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  // Features Section
  const FeaturesSection = () => {
    const icons = [Shield, Wifi, Thermometer, Eye, Key, Building, Trees, Coffee, Heart, Users];
    
    return (
      <section id="features" className="section-padding container-custom">
        <h2 className="text-3xl font-bold mb-8">{t.labels.features}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listingData.features.map((feature, idx) => {
            const Icon = icons[idx % icons.length];
            return (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-3 card"
              >
                <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span>{feature}</span>
              </motion.div>
            );
          })}
        </div>
      </section>
    );
  };

  // Description Section
  const Description = () => (
    <section className="section-padding container-custom">
      <h2 className="text-3xl font-bold mb-8">{t.labels.description}</h2>
      <div className="card">
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {listingData.descriptionLong.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  );

  // Map Section
  const MapSection = () => (
    <section id="location" className="section-padding container-custom">
      <h2 className="text-3xl font-bold mb-8">{t.nav.location}</h2>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="h-[400px] rounded-xl overflow-hidden">
            <MapContainer 
              center={[listingData.coordinates.lat, listingData.coordinates.lng]} 
              zoom={15} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[listingData.coordinates.lat, listingData.coordinates.lng]}>
                <Popup>
                  <div>
                    <h3 className="font-bold">{listingData.title}</h3>
                    <p>{listingData.address.street}</p>
                    <p>{listingData.address.city}, {listingData.address.postal}</p>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
          <button
            onClick={() => window.open(`https://maps.google.com/?q=${listingData.coordinates.lat},${listingData.coordinates.lng}`, '_blank')}
            className="btn-primary mt-4 flex items-center justify-center gap-2"
          >
            <Navigation className="w-5 h-5" />
            {t.cta.getDirections}
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-bold mb-3">{t.labels.nearbyPlaces}</h3>
            <ul className="space-y-2 text-sm">
              {listingData.neighborhood.highlights.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="card">
            <h3 className="font-bold mb-3">{t.labels.transport}</h3>
            <div className="space-y-2 text-sm">
              {listingData.neighborhood.transport.bus !== 'N/A' && (
                <div className="flex items-center gap-2">
                  <Train className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span>Busz: {listingData.neighborhood.transport.bus}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // FAQ Section - IMPROVED CONSISTENCY
  const FAQSection = () => (
    <section className="section-padding container-custom">
      <h2 className="text-3xl font-bold mb-8">{t.labels.faq}</h2>
      <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {listingData.faq.map((item, idx) => (
          <div key={idx} className="card">
            <h3 className="font-bold mb-3 text-blue-600 dark:text-blue-400">
              {item.question}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </section>
  );

  // Contact Section - IMPROVED & SIMPLIFIED
  const ContactSection = () => (
    <section id="contact" className="section-padding container-custom">
      <h2 className="text-3xl font-bold mb-8 text-center">{t.labels.contactForm}</h2>
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Direct Contact */}
        <div className="card">
          <h3 className="font-bold mb-2 text-lg">Közvetlen kapcsolat</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{listingData.contact.name}</p>
          <div className="space-y-4">
            <a 
              href={`tel:${listingData.contact.phone}`} 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span>{listingData.contact.phone}</span>
            </a>
            <a 
              href={`mailto:${listingData.contact.email}`} 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span>{listingData.contact.email}</span>
            </a>
            <a 
              href={`https://wa.me/${listingData.contact.whatsapp}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
        
        {/* Documents */}
        {listingData.documents.length > 0 && (
          <div className="card">
            <h3 className="font-bold mb-4 text-lg">{t.labels.documents}</h3>
            <div className="space-y-4">
              {listingData.documents.map((doc, idx) => (
                <a
                  key={idx}
                  href={doc.url}
                  download
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span>{doc.name}</span>
                  </div>
                  <Download className="w-4 h-4 text-gray-400" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );

  // Scroll to top button
  const ScrollToTop = () => (
    <AnimatePresence>
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-20 right-4 md:bottom-8 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-30"
        >
          <ArrowUp className="w-6 h-6" />
        </motion.button>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* <Helmet>
        <title>{listingData.priceFormatted} • {listingData.sizeSqm} m² • {listingData.address.city} {listingData.address.street} – Eladó</title>
        <meta name="description" content={listingData.descriptionShort.join('. ')} />
        <meta property="og:title" content={`${listingData.title} - ${listingData.priceFormatted}`} />
        <meta property="og:description" content={listingData.tagline} />
        <meta property="og:image" content={listingData.photos[0]} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={window.location.href} />
      </Helmet> */}
      
      <Header />
      <main className="pt-16">
        <Hero />
        <FactsGrid />
        <Description />
        <FeaturesSection />
        <Gallery />
        <VideoSection />
        <MapSection />
        <FAQSection />
        <ContactSection />
      </main>
      
      <footer className="bg-gray-100 dark:bg-gray-800 py-8 px-4 text-center text-sm text-gray-600 dark:text-gray-400 mb-16 md:mb-0">
        <p>{listingData.title} • {listingData.address.street}, {listingData.address.city}</p>
        <p className="mt-2">© 2025 Minden jog fenntartva</p>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
          Weboldal: <a href="https://www.linkedin.com/in/zsombor-k%C3%B6bli/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Köbli Zsombor</a>
        </p>
      </footer>
      
      <MobileCTA />
      <ScrollToTop />
      
      {/* JSON-LD Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Apartment",
          "name": listingData.title,
          "description": listingData.tagline,
          "image": listingData.photos,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": listingData.address.street,
            "addressLocality": listingData.address.city,
            "postalCode": listingData.address.postal,
            "addressCountry": listingData.address.country
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": listingData.coordinates.lat,
            "longitude": listingData.coordinates.lng
          },
          "floorSize": {
            "@type": "QuantitativeValue",
            "value": listingData.sizeSqm,
            "unitCode": "MTK"
          },
          "numberOfRooms": listingData.rooms,
          "offers": {
            "@type": "Offer",
            "priceCurrency": "HUF",
            "price": listingData.priceHUF,
            "availability": "https://schema.org/InStock"
          }
        })}
      </script>
    </div>
  );
}

export default App;